import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * H1_AlignRight_Desc Component
 * Displays a heading (H1) and description, both right-aligned
 * Supports links/CTAs within the description text
 */
const H1AlignRightDesc = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "", component_name = "H1_AlignRight_Desc",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
  }, [id, title, description]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
    });
    setIsEditing(false);
  };

  // Helper function to parse description and convert links to clickable elements
  const parseDescription = (text) => {
    if (!text) return "";
    
    // Simple HTML escape function
    const escapeHtml = (str) => {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return str.replace(/[&<>"']/g, (m) => map[m]);
    };
    
    // Check if text already contains HTML links
    if (text.includes('<a ') || text.includes('<A ')) {
      // Text already has HTML, return as is
      return text;
    }
    
    // Escape the text first
    let processedText = escapeHtml(text);
    
    // Simple regex to detect URLs and phone numbers (not already in links)
    const urlRegex = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi;
    const phoneRegex = /(\+?\d{1,4}[\s-]?\(?\d{1,4}\)?[\s-]?\d{1,4}[\s-]?\d{1,4}[\s-]?\d{1,9})/g;
    
    // Replace URLs with links
    processedText = processedText.replace(urlRegex, (url) => {
      const href = url.startsWith('http') ? url : `https://${url}`;
      const linkColor = styleSettings?.secondaryColor || '#a1894c';
      return `<a href="${escapeHtml(href)}" style="color: ${linkColor}; text-decoration: underline;">${url}</a>`;
    });
    
    // Replace phone numbers with tel: links
    processedText = processedText.replace(phoneRegex, (phone) => {
      const cleanPhone = phone.replace(/[\s-()]/g, '');
      const linkColor = styleSettings?.secondaryColor || '#a1894c';
      return `<a href="tel:${cleanPhone}" style="color: ${linkColor}; text-decoration: underline;">${phone}</a>`;
    });
    
    return processedText;
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component h1-align-right-desc-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Right-Aligned Heading & Description</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Heading (H1)</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter heading"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description (URLs and phone numbers will be automatically linked)"
            rows={5}
          />
          <small className="text-muted">
            Tip: URLs and phone numbers in the description will be automatically converted to clickable links.
          </small>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const processedDescription = parseDescription(localDescription);

  return (
    <>
      {!previewMode && (
        <div className="email-component h1-align-right-desc mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="right"
            style={{
              padding: "24px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            <div
              style={{
                textAlign: "right",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              {/* Heading (H1) */}
              {localTitle && (
                <h1
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: primaryColor || "#333333",
                    marginBottom: "16px",
                    marginTop: "0",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    textAlign: "right",
                  }}
                >
                  {localTitle}
                </h1>
              )}

              {/* Description */}
              {localDescription && (
                <div
                  style={{
                    fontSize: "16px",
                    color: primaryColor || "#333333",
                    lineHeight: "1.6",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    textAlign: "right",
                  }}
                  dangerouslySetInnerHTML={{ __html: processedDescription }}
                />
              )}
            </div>
          </td>
        </tr>
      </table>
    </>
  );
};

export default H1AlignRightDesc;

